import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSystem } from '@core';
import Icon from '../Icon';

interface Position {
	x: number;
	y: number;
}

interface IMenuItem {
	label: string;
	action: (item: IMenuItem, props) => void;
	content?: () => React.ReactNode;
	shortcutKey?: string;
	iconRight?: string;
	subContent?: (props: withAppTypes<{ onClose: () => void }>) => React.ReactNode;
}

interface ContextMenuProps {
	items: IMenuItem[];
	defaultPosition: Position;
	onClose?: () => void;
}

const ContextMenu = ({ items, ...props }: withAppTypes<ContextMenuProps>) => {
	const contextMenuRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!contextMenuRef?.current) {
			return;
		}

		const contextMenu = contextMenuRef.current;

		const boundingClientRect = contextMenu.getBoundingClientRect();
		if (boundingClientRect.bottom + boundingClientRect.height > window.innerHeight) {
			props.defaultPosition.y = props.defaultPosition.y - boundingClientRect.height;
		}
		if (boundingClientRect.right + boundingClientRect.width > window.innerWidth) {
			props.defaultPosition.x = props.defaultPosition.x - boundingClientRect.width;
		}
	}, [props.defaultPosition]);

	if (!items) {
		return null;
	}

	return (
		<div
			data-cy="context-menu"
			className="relative z-50 block w-44 gap-1 rounded border-common-dark bg-secondary-dark px-1 py-2"
			onContextMenu={(e) => e.preventDefault()}
		>
			{items.map((item, index) => (
				<div key={index} className="relative">
					{item?.subContent ? <MenuItemWithSubContent item={item} {...props} /> : <MenuItem item={item} {...props} />}
				</div>
			))}
		</div>
	);
};

const MenuItemWithSubContent = ({ item, ...props }: { item: IMenuItem } & Omit<ContextMenuProps, 'items'>) => {
	const { commandsManager, servicesManager } = useSystem();
	const [isShowSubContent, setIsShowSubContent] = useState(false);
	const onMouseEnterItem = useCallback(() => {
		setIsShowSubContent(true);
	}, []);

	const onMouseLeaveItem = useCallback(() => {
		setIsShowSubContent(false);
	}, []);

	return (
		<div className="relative" onMouseLeave={onMouseLeaveItem} onMouseEnter={onMouseEnterItem}>
			<MenuItem item={item} {...props} />
			<div className={classNames('absolute left-full top-0', !isShowSubContent && 'hidden')}>
				{item.subContent({
					commandsManager,
					servicesManager,
					onClose: props.onClose
				})}
			</div>
		</div>
	);
};

interface MenuItemProps {
	item: IMenuItem;
}

const MenuItem = ({ item, ...props }: MenuItemProps) => {
	return item?.content ? (
		item?.content()
	) : (
		<div
			data-cy="context-menu-item"
			onClick={() => item.action(item, props)}
			style={{
				justifyContent: 'space-between'
			}}
			className="flex cursor-pointer items-center px-2 py-1 text-xs text-common-bright transition duration-300 hover:bg-primary-light"
		>
			<span>{item?.label}</span>
			{item.shortcutKey && <span>({item.shortcutKey})</span>}
			{item.iconRight && <Icon name={item.iconRight} className="inline" />}
		</div>
	);
};

export default ContextMenu;
